import { parseSourceLink } from './source-links';
import type { TaskSourceKind } from './types/task-sources';

/**
 * SF-T-76: the people and tasks a mirrored task's text names on its source, as that source writes them:
 * - asana: profile links (`/1/<ws>/profile/<gid>`, `/0/profile/<gid>`, and the old `/0/<gid>/` a plain-text
 *   mention becomes) and task links;
 * - trello: `@username` and card links (`trello.com/c/<shortLink>`);
 * - github: `@login`, `#12`, `owner/repo#12` and issue links (not pull requests), in the source's own repo;
 * - jflo: `@[Name](user:<id>)`, `T-424` and task links (`<host>/tasks/<id>`) on the source's own host.
 * The sync reads each one from the source once and keeps it (task_source_ref); the task page shows it by
 * name or title with the source's mark. This file only says where they are and what they point at.
 */
export type SourceRefType = 'person' | 'task';

export interface SourceRefKey {
  type: SourceRefType;
  /** How it's kept: an Asana gid, a lower-case handle (Trello, GitHub), a Trello short link, an issue number, a jflo id or `T-424`. */
  key: string;
}

/** What the text is read against: the source's kind, and its repo (GitHub) or API root (jflo). */
export interface SourceRefScope {
  kind: TaskSourceKind;
  repo?: string | null;
  url?: string | null;
}

export interface SourceRefRange extends SourceRefKey {
  start: number;
  end: number;
  /** The name the text itself carries (a jflo mention's), shown until the source's is known. */
  label?: string;
}

/** Web addresses in text, as the task page links them. */
export const SOURCE_URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

const ASANA_ID = /^\d{1,30}$/;
// Not `@acme/ui` (a scoped package) or an email address.
const HANDLE_RE = /(?<![\w@./-])@([A-Za-z0-9][A-Za-z0-9_-]{0,38})(?![\w@/-])/g;
const GITHUB_ISSUE_RE = /(?<![\w/#&.-])(?:([A-Za-z0-9-]{1,39}\/[A-Za-z0-9._-]{1,100}))?#(\d{1,9})(?![\w-])/g;
// Plain `T-424` only: `JF-T-18` is a factory ref (a project's prefix), linked as one already.
const JFLO_REF_RE = /(?<![A-Za-z0-9-])T-(\d{1,9})(?![A-Za-z0-9])/g;
const JFLO_MENTION_RE = /@\[([^\]\n]{1,200})\]\(user:([\w-]{1,100})\)/g;

const sameText = (a: string | null | undefined, b: string | null | undefined) => Boolean(a && b && a.toLowerCase() === b.toLowerCase());

function hostOf(value: string | null | undefined): string | null {
  try {
    return value ? new URL(value).hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** What a web address points at on the source, or null when it isn't one of its people or tasks. */
export function sourceRefOfUrl(scope: SourceRefScope, href: string): SourceRefKey | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const parts = url.pathname.split('/').filter(Boolean);
  switch (scope.kind) {
    case 'asana': {
      if (host !== 'app.asana.com') return null;
      // The old form a plain-text mention takes: `/0/<person>/` (or `/0/<person>/list`, their tasks).
      if (parts[0] === '0' && ASANA_ID.test(parts[1] ?? '') && (parts.length === 2 || (parts.length === 3 && parts[2] === 'list'))) {
        return { type: 'person', key: parts[1] };
      }
      const link = parseSourceLink(href);
      return link?.kind === 'asana' ? { type: link.type === 'person' ? 'person' : 'task', key: link.id } : null;
    }
    case 'trello': {
      const link = parseSourceLink(href);
      return link?.kind === 'trello' ? { type: 'task', key: link.id } : null;
    }
    case 'github': {
      // Issues only: the adapter's get() answers null for a pull request, so its link stays as written.
      if (host !== 'github.com' || parts.length !== 4 || parts[2] !== 'issues') return null;
      if (!sameText(`${parts[0]}/${parts[1]}`, scope.repo) || !/^\d{1,9}$/.test(parts[3])) return null;
      return { type: 'task', key: String(Number(parts[3])) };
    }
    case 'jflo': {
      const own = hostOf(scope.url);
      if (!own || host !== own.replace(/^www\./, '') || parts.length !== 2 || parts[0] !== 'tasks' || !/^[\w-]{1,100}$/.test(parts[1])) return null;
      return { type: 'task', key: parts[1] };
    }
  }
}

/** The people and tasks named in a run of text with no web addresses in it, in order. */
export function sourceTextRefs(scope: SourceRefScope, text: string): SourceRefRange[] {
  const out: SourceRefRange[] = [];
  const at = (m: RegExpMatchArray) => m.index ?? 0;
  switch (scope.kind) {
    case 'asana':
      return out;
    case 'trello':
      for (const m of text.matchAll(HANDLE_RE)) out.push({ type: 'person', key: m[1].toLowerCase(), start: at(m), end: at(m) + m[0].length });
      return out;
    case 'github':
      for (const m of text.matchAll(HANDLE_RE)) out.push({ type: 'person', key: m[1].toLowerCase(), start: at(m), end: at(m) + m[0].length });
      for (const m of text.matchAll(GITHUB_ISSUE_RE)) {
        if (m[1] && !sameText(m[1], scope.repo)) continue;
        out.push({ type: 'task', key: String(Number(m[2])), start: at(m), end: at(m) + m[0].length });
      }
      return out.sort((a, b) => a.start - b.start);
    case 'jflo':
      for (const m of text.matchAll(JFLO_MENTION_RE)) out.push({ type: 'person', key: m[2], label: m[1].trim(), start: at(m), end: at(m) + m[0].length });
      for (const m of text.matchAll(JFLO_REF_RE)) {
        const start = at(m);
        if (out.some((r) => start >= r.start && start < r.end)) continue;
        out.push({ type: 'task', key: `T-${Number(m[1])}`, start, end: start + m[0].length });
      }
      return out.sort((a, b) => a.start - b.start);
  }
}

/** Every person and task the whole text names on the source, once each: what the sync reads and keeps. */
export function findSourceRefs(scope: SourceRefScope, text: string): SourceRefKey[] {
  const found = new Map<string, SourceRefKey>();
  const add = (ref: SourceRefKey) => found.set(sourceRefId(ref), { type: ref.type, key: ref.key });
  text.split(SOURCE_URL_RE).forEach((part, i) => {
    if (i % 2 === 1) {
      const ref = sourceRefOfUrl(scope, part);
      if (ref) add(ref);
    } else {
      for (const ref of sourceTextRefs(scope, part)) add(ref);
    }
  });
  return [...found.values()];
}

/** "person:123": one key for a person or task, for maps. */
export function sourceRefId(ref: SourceRefKey): string {
  return `${ref.type}:${ref.key}`;
}
